import { soundAction } from '@tanky/shared';
import { api } from '../api';
import { encodeWav } from './wav';

/**
 * Výchozí sada zvuků: dřívější syntéza (Web Audio) vyrenderovaná do WAV.
 * Používá se jen tlačítkem „Vygenerovat výchozí sadu“ na stránce Zvuky –
 * ve hře se pak přehrávají soubory jako každé jiné.
 */

const RATE = 48000;

interface Out {
  ctx: BaseAudioContext;
  dest: AudioNode;
  t: number;
  noise: AudioBuffer;
}

function env(o: Out, t: number, peak: number, attack: number, decay: number): GainNode {
  const g = o.ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(o.dest);
  return g;
}

function noiseBurst(
  o: Out,
  p: { type: BiquadFilterType; f0: number; f1: number; q?: number; peak: number; attack?: number; decay: number; delay?: number },
) {
  const t = o.t + (p.delay ?? 0);
  const attack = p.attack ?? 0.005;
  const src = o.ctx.createBufferSource();
  src.buffer = o.noise;
  src.loop = true;
  const f = o.ctx.createBiquadFilter();
  f.type = p.type;
  f.Q.value = p.q ?? 0.8;
  f.frequency.setValueAtTime(p.f0, t);
  f.frequency.exponentialRampToValueAtTime(Math.max(20, p.f1), t + attack + p.decay);
  src.connect(f).connect(env(o, t, p.peak, attack, p.decay));
  src.start(t, Math.random() * 0.5);
  src.stop(t + attack + p.decay + 0.05);
}

function tone(o: Out, p: { type?: OscillatorType; f0: number; f1?: number; peak: number; attack?: number; decay: number; delay?: number }) {
  const t = o.t + (p.delay ?? 0);
  const attack = p.attack ?? 0.005;
  const osc = o.ctx.createOscillator();
  osc.type = p.type ?? 'sine';
  osc.frequency.setValueAtTime(p.f0, t);
  if (p.f1) osc.frequency.exponentialRampToValueAtTime(p.f1, t + attack + p.decay);
  osc.connect(env(o, t, p.peak, attack, p.decay));
  osc.start(t);
  osc.stop(t + attack + p.decay + 0.05);
}

/** Smyčka se stálou hlasitostí (začátek a konec na sebe navazují). */
function steadyNoise(o: Out, type: BiquadFilterType, freq: number, q: number, gain: number, len: number) {
  const src = o.ctx.createBufferSource();
  src.buffer = o.noise;
  src.loop = true;
  const f = o.ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = o.ctx.createGain();
  g.gain.value = gain;
  src.connect(f).connect(g).connect(o.dest);
  src.start(0);
  src.stop(len);
}

function steadyTone(o: Out, type: OscillatorType, freq: number, gain: number, len: number, am?: { freq: number; depth: number }) {
  const osc = o.ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const g = o.ctx.createGain();
  g.gain.value = gain;
  osc.connect(g).connect(o.dest);
  if (am) {
    const lfo = o.ctx.createOscillator();
    lfo.frequency.value = am.freq;
    const depth = o.ctx.createGain();
    depth.gain.value = gain * am.depth;
    lfo.connect(depth).connect(g.gain);
    lfo.start(0);
    lfo.stop(len);
  }
  osc.start(0);
  osc.stop(len);
}

function shot(o: Out, heavy: boolean) {
  noiseBurst(o, { type: 'lowpass', f0: heavy ? 1400 : 2600, f1: 200, peak: heavy ? 0.9 : 0.65, decay: heavy ? 0.45 : 0.28 });
  tone(o, { f0: heavy ? 110 : 160, f1: 45, peak: heavy ? 0.7 : 0.45, decay: heavy ? 0.4 : 0.25 });
}

function launch(o: Out, banana: boolean) {
  noiseBurst(o, { type: 'bandpass', f0: 900, f1: 300, peak: 0.35, decay: 0.18 });
  tone(o, { f0: banana ? 500 : 220, f1: banana ? 900 : 90, peak: 0.25, decay: 0.2 });
}

function explosion(o: Out, r: number) {
  const k = Math.min(1, r / 110);
  const decay = 0.35 + k * 1.6;
  noiseBurst(o, { type: 'lowpass', f0: 3000 - k * 1800, f1: 60, peak: 0.6 + k * 0.5, attack: 0.004, decay });
  tone(o, { f0: 90 - k * 40, f1: 28, peak: 0.6 + k * 0.4, decay: decay * 0.8 });
  if (r >= 80) noiseBurst(o, { type: 'lowpass', f0: 400, f1: 40, peak: 0.7, attack: 0.3, decay: 3, delay: 0.1 });
}

interface DefaultSound {
  action: string;
  duration: number;
  render(o: Out): void;
}

export const DEFAULT_SOUNDS: DefaultSound[] = [
  { action: 'fire.default', duration: 0.5, render: (o) => shot(o, false) },
  { action: 'fire.heavy', duration: 0.6, render: (o) => shot(o, true) },
  { action: 'fire.nuke', duration: 0.6, render: (o) => shot(o, true) },
  {
    action: 'fire.laser',
    duration: 0.6,
    render: (o) => {
      tone(o, { type: 'sawtooth', f0: 2400, f1: 180, peak: 0.25, decay: 0.45 });
      tone(o, { type: 'square', f0: 1200, f1: 90, peak: 0.08, decay: 0.4 });
    },
  },
  {
    action: 'fire.shotgun',
    duration: 0.4,
    render: (o) => {
      noiseBurst(o, { type: 'highpass', f0: 1200, f1: 400, peak: 0.8, decay: 0.2 });
      tone(o, { f0: 200, f1: 60, peak: 0.5, decay: 0.18 });
    },
  },
  {
    action: 'fire.sheep',
    duration: 0.6,
    render: (o) => {
      tone(o, { type: 'sawtooth', f0: 520, f1: 480, peak: 0.12, attack: 0.03, decay: 0.45 });
      tone(o, { type: 'sine', f0: 540, f1: 500, peak: 0.08, attack: 0.03, decay: 0.45 });
    },
  },
  { action: 'fire.bunker', duration: 0.8, render: (o) => noiseBurst(o, { type: 'bandpass', f0: 600, f1: 1800, q: 3, peak: 0.3, attack: 0.05, decay: 0.6 }) },
  {
    action: 'fire.bazuka',
    duration: 0.7,
    render: (o) => {
      noiseBurst(o, { type: 'bandpass', f0: 1600, f1: 400, q: 2, peak: 0.5, attack: 0.01, decay: 0.5 });
      noiseBurst(o, { type: 'lowpass', f0: 600, f1: 150, peak: 0.7, attack: 0.005, decay: 0.35 });
      tone(o, { type: 'sawtooth', f0: 380, f1: 160, peak: 0.25, attack: 0.02, decay: 0.4 });
    },
  },
  {
    action: 'fire.bomber',
    duration: 1.2,
    render: (o) => {
      steadyNoise(o, 'lowpass', 400, 1.2, 0.4, 1.2);
      steadyTone(o, 'sawtooth', 75, 0.2, 1.2, { freq: 8, depth: 0.3 });
      noiseBurst(o, { type: 'bandpass', f0: 800, f1: 300, peak: 0.3, attack: 0.1, decay: 0.6 });
    },
  },
  ...(['teleport', 'napalm', 'airstrike', 'dirt'] as const).map((w): DefaultSound => ({ action: `fire.${w}`, duration: 0.35, render: (o) => launch(o, false) })),
  { action: 'fire.banana', duration: 0.35, render: (o) => launch(o, true) },
  { action: 'explosion.small', duration: 0.9, render: (o) => explosion(o, 22) },
  { action: 'explosion.medium', duration: 1.4, render: (o) => explosion(o, 42) },
  { action: 'explosion.large', duration: 1.9, render: (o) => explosion(o, 65) },
  { action: 'explosion.nuke', duration: 3.8, render: (o) => explosion(o, 110) },
  {
    action: 'explosion.dirt',
    duration: 1.4,
    render: (o) => noiseBurst(o, { type: 'lowpass', f0: 500, f1: 120, peak: 0.3, attack: 0.15, decay: 1, delay: 0.1 }),
  },
  { action: 'whistle', duration: 2.2, render: (o) => tone(o, { f0: 1800, f1: 500, peak: 0.07, attack: 0.3, decay: 1.8 }) },
  {
    action: 'burn',
    duration: 2,
    render: (o) => {
      steadyNoise(o, 'lowpass', 500, 0.7, 0.1, 2);
      for (let i = 0; i < 12; i++) {
        noiseBurst(o, { type: 'bandpass', f0: 1500 + Math.random() * 2500, f1: 800, q: 2, peak: 0.08 + Math.random() * 0.08, decay: 0.05 + Math.random() * 0.08, delay: i / 6 + Math.random() * 0.05 });
      }
    },
  },
  {
    action: 'slide',
    duration: 1.4,
    render: (o) => noiseBurst(o, { type: 'lowpass', f0: 500, f1: 120, peak: 0.35, attack: 0.15, decay: 1.1, delay: 0.05 }),
  },
  {
    action: 'splash',
    duration: 0.7,
    render: (o) => {
      noiseBurst(o, { type: 'highpass', f0: 1800, f1: 600, peak: 0.35, decay: 0.55 });
      noiseBurst(o, { type: 'bandpass', f0: 500, f1: 200, peak: 0.25, decay: 0.3, delay: 0.05 });
    },
  },
  {
    action: 'teleport',
    duration: 0.55,
    render: (o) => [523, 784, 1047, 1568].forEach((f, i) => tone(o, { type: 'triangle', f0: f, peak: 0.15, decay: 0.25, delay: i * 0.06 })),
  },
  { action: 'hit', duration: 0.25, render: (o) => tone(o, { type: 'square', f0: 240, f1: 90, peak: 0.12, decay: 0.15 }) },
  { action: 'death', duration: 0.9, render: (o) => tone(o, { type: 'sawtooth', f0: 400, f1: 60, peak: 0.18, decay: 0.8 }) },
  {
    action: 'mine.beep',
    duration: 0.3,
    render: (o) => {
      tone(o, { type: 'square', f0: 1760, peak: 0.08, decay: 0.06 });
      tone(o, { type: 'square', f0: 1760, peak: 0.08, decay: 0.06, delay: 0.14 });
    },
  },
  { action: 'marker', duration: 1, render: (o) => noiseBurst(o, { type: 'highpass', f0: 3000, f1: 1500, peak: 0.18, attack: 0.05, decay: 0.9 }) },
  {
    action: 'blackhole',
    duration: 2.4,
    render: (o) => {
      tone(o, { type: 'sawtooth', f0: 220, f1: 40, peak: 0.12, attack: 0.3, decay: 2 });
      noiseBurst(o, { type: 'lowpass', f0: 1200, f1: 80, peak: 0.25, attack: 0.4, decay: 2 });
    },
  },
  {
    action: 'thunder',
    duration: 1.8,
    render: (o) => {
      noiseBurst(o, { type: 'highpass', f0: 3000, f1: 800, peak: 0.6, attack: 0.002, decay: 0.15 });
      noiseBurst(o, { type: 'lowpass', f0: 900, f1: 60, peak: 0.8, attack: 0.05, decay: 1.6, delay: 0.08 });
    },
  },
  {
    action: 'quake',
    duration: 1.9,
    render: (o) => {
      noiseBurst(o, { type: 'lowpass', f0: 180, f1: 50, peak: 0.9, attack: 0.2, decay: 1.6 });
      tone(o, { f0: 45, f1: 30, peak: 0.5, attack: 0.2, decay: 1.6 });
    },
  },
  {
    action: 'engine',
    duration: 2,
    render: (o) => {
      steadyNoise(o, 'bandpass', 1100, 1.2, 0.16, 2);
      steadyTone(o, 'sawtooth', 95, 0.05, 2);
    },
  },
  {
    action: 'drone',
    duration: 1.5,
    render: (o) => {
      // Bzučení rotorů: dva tóny s kmitáním a vzdušný šum
      steadyTone(o, 'sawtooth', 180, 0.05, 1.5, { freq: 12, depth: 0.4 });
      steadyTone(o, 'triangle', 362, 0.04, 1.5, { freq: 14, depth: 0.5 });
      steadyNoise(o, 'bandpass', 2400, 1.5, 0.05, 1.5);
    },
  },
  {
    action: 'your_turn',
    duration: 0.6,
    render: (o) => {
      tone(o, { f0: 660, peak: 0.25, decay: 0.25 });
      tone(o, { f0: 990, peak: 0.25, decay: 0.4, delay: 0.14 });
    },
  },
  {
    action: 'chat',
    duration: 0.3,
    render: (o) => {
      tone(o, { f0: 1320, peak: 0.12, decay: 0.12 });
      tone(o, { f0: 1760, peak: 0.1, decay: 0.15, delay: 0.07 });
    },
  },
  {
    action: 'win',
    duration: 1.4,
    render: (o) => [523, 659, 784, 1047].forEach((f, i) => tone(o, { type: 'triangle', f0: f, peak: 0.22, decay: i === 3 ? 0.8 : 0.2, delay: i * 0.13 })),
  },
  {
    action: 'emote.blink',
    duration: 0.4,
    render: (o) => {
      tone(o, { type: 'square', f0: 1400, peak: 0.06, decay: 0.05 });
      tone(o, { type: 'square', f0: 1400, peak: 0.06, decay: 0.05, delay: 0.25 });
    },
  },
  { action: 'emote.flare', duration: 0.8, render: (o) => noiseBurst(o, { type: 'bandpass', f0: 600, f1: 3000, peak: 0.25, attack: 0.05, decay: 0.6 }) },
  { action: 'emote.smoke', duration: 1.6, render: (o) => noiseBurst(o, { type: 'highpass', f0: 2500, f1: 1200, peak: 0.2, attack: 0.1, decay: 1.4 }) },
  {
    action: 'emote.firework',
    duration: 1.8,
    render: (o) => {
      tone(o, { f0: 400, f1: 2400, peak: 0.08, attack: 0.05, decay: 0.7 });
      noiseBurst(o, { type: 'lowpass', f0: 4000, f1: 200, peak: 0.5, decay: 0.6, delay: 0.75 });
      for (let i = 0; i < 8; i++) noiseBurst(o, { type: 'highpass', f0: 5000, f1: 3000, peak: 0.08, decay: 0.04, delay: 0.9 + i * 0.07 + Math.random() * 0.05 });
    },
  },
  {
    action: 'emote.hop',
    duration: 0.6,
    render: (o) => {
      tone(o, { f0: 220, f1: 520, peak: 0.2, decay: 0.2 });
      tone(o, { f0: 480, f1: 200, peak: 0.12, decay: 0.2, delay: 0.3 });
    },
  },
  {
    action: 'emote.wave',
    duration: 0.45,
    render: (o) => {
      tone(o, { type: 'triangle', f0: 700, f1: 900, peak: 0.08, decay: 0.15 });
      tone(o, { type: 'triangle', f0: 900, f1: 700, peak: 0.08, decay: 0.15, delay: 0.18 });
    },
  },
];

/** Vyrenderuje jeden výchozí zvuk (mono, 48 kHz). */
export async function renderDefault(d: DefaultSound): Promise<Float32Array> {
  const len = Math.ceil(RATE * d.duration);
  const ctx = new OfflineAudioContext(1, len, RATE);
  const noise = ctx.createBuffer(1, RATE, RATE);
  const nd = noise.getChannelData(0);
  for (let i = 0; i < RATE; i++) nd[i] = Math.random() * 2 - 1;
  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(ctx.destination);
  d.render({ ctx, dest: master, t: 0.001, noise });
  const out = await ctx.startRendering();
  return out.getChannelData(0);
}

/**
 * Vygeneruje a nahraje výchozí zvuky pro akce, které ještě žádný zvuk nemají.
 * Vrací počet nahraných.
 */
export async function generateDefaults(hasBinding: (action: string) => boolean, onProgress: (done: number, total: number) => void): Promise<number> {
  const todo = DEFAULT_SOUNDS.filter((d) => !hasBinding(d.action) && soundAction(d.action));
  let done = 0;
  for (const d of todo) {
    const data = await renderDefault(d);
    const wav = encodeWav([data], RATE);
    const name = `Výchozí – ${soundAction(d.action)!.name}`;
    const fileName = `${d.action}.wav`;
    const s = await api.uploadSound(wav, name, 1, d.duration, fileName);
    await api.setBinding(d.action, [{ soundId: s.id, volume: 1 }]);
    onProgress(++done, todo.length);
  }
  return done;
}

export function hasDefaultSound(action: string): boolean {
  return DEFAULT_SOUNDS.some((d) => d.action === action);
}

export async function generateDefaultSound(action: string): Promise<boolean> {
  const d = DEFAULT_SOUNDS.find((x) => x.action === action);
  if (!d || !soundAction(d.action)) return false;
  const data = await renderDefault(d);
  const wav = encodeWav([data], RATE);
  const name = `Výchozí – ${soundAction(d.action)!.name}`;
  const fileName = `${d.action}.wav`;
  const s = await api.uploadSound(wav, name, 1, d.duration, fileName);
  await api.setBinding(d.action, [{ soundId: s.id, volume: 1 }]);
  return true;
}
